import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  List,
  ListItem,
  Button
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { getChildProgress, getChildEnrollments, getChildren } from '../../services/parentService';
import { getRankedProfile, getRatingHistory, getQuizResults } from '../../services/rankedService';
import { Child, ChildProgress, ChildEnrollment } from '../../types/parent';
import { QuizResultRow, RankedProfile, RatingHistoryEvent } from '../../types/ranked';
import RankedProfilePanel from '../../components/ranked/RankedProfilePanel';
import QuizResultsTable from '../../components/ranked/QuizResultsTable';

function ChildDetailPage() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();

  const [child, setChild] = useState<Child | null>(null);
  const [progress, setProgress] = useState<ChildProgress | null>(null);
  const [enrollments, setEnrollments] = useState<ChildEnrollment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [rankedProfile, setRankedProfile] = useState<RankedProfile | null>(null);
  const [ratingHistory, setRatingHistory] = useState<RatingHistoryEvent[]>([]);
  const [quizResults, setQuizResults] = useState<QuizResultRow[]>([]);
  const [rankedLoading, setRankedLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchChildData = async () => {
      if (!childId) {
        setError('Child ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const childIdNum = parseInt(childId, 10);

        // Fetch child info from children list
        const childrenData = await getChildren();
        const childData = childrenData.find(c => c.id === childIdNum);

        if (!childData) {
          setError('Child not found');
          setLoading(false);
          return;
        }

        setChild(childData);

        // Fetch progress and enrollments in parallel
        const [progressData, enrollmentsData] = await Promise.all([
          getChildProgress(childIdNum),
          getChildEnrollments(childIdNum)
        ]);

        setProgress(progressData);
        setEnrollments(enrollmentsData);

        // Ranked data is fetched separately and its failure is swallowed: a
        // season with no ranked activity, or a ranked outage, must not blank
        // the enrollment and progress information a parent came here for.
        setRankedLoading(true);
        try {
          const [profileData, historyData, resultsData] = await Promise.all([
            getRankedProfile(childIdNum),
            getRatingHistory(childIdNum),
            getQuizResults(childIdNum)
          ]);
          setRankedProfile(profileData);
          setRatingHistory(historyData);
          setQuizResults(resultsData);
        } catch (err) {
          console.error('Error fetching ranked data:', err);
        } finally {
          setRankedLoading(false);
        }
      } catch (err) {
        console.error('Error fetching child data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load child data');
      } finally {
        setLoading(false);
      }
    };

    fetchChildData();
  }, [childId]);

  // Helper to format date
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  // Helper to get enrollment status
  const getEnrollmentStatus = (enrollment: ChildEnrollment): { label: string; color: 'success' | 'warning' | 'error' | 'default' } => {
    if (enrollment.paymentStatus === 'completed') {
      return { label: 'Active', color: 'success' };
    } else if (enrollment.paymentStatus === 'pending') {
      return { label: 'Pending Payment', color: 'warning' };
    } else if (enrollment.paymentStatus === 'failed') {
      return { label: 'Payment Failed', color: 'error' };
    }
    return { label: 'Unknown', color: 'default' };
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (error || !child) {
    return (
      <MainLayout>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Child not found'}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/parent/dashboard')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Header with Back Button */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/parent/dashboard')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>

        {/* Child Profile Header */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                bgcolor: 'primary.main',
                fontSize: '2rem'
              }}
            >
              {child.firstName?.charAt(0)}{child.lastName?.charAt(0)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom data-testid="child-name">
                {child.firstName} {child.lastName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip icon={<SchoolIcon />} label={child.email} />
                {progress?.grade && (
                  <Chip
                    icon={<SchoolIcon />}
                    label={`Grade ${progress.grade}`}
                    color="primary"
                  />
                )}
                {progress?.school && (
                  <Chip label={progress.school} variant="outlined" />
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Main Content */}
      <Grid container spacing={4}>
        {/* Left Column - Progress & Enrollments */}
        <Grid item xs={12} md={8}>
          {/* Ranked standing. Third-person voice throughout — this is a parent
              reading about their child, not a student reading about themselves. */}
          {rankedProfile && (
            <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
              <CardHeader title="Ranked Mathlete" />
              <CardContent>
                <RankedProfilePanel
                  profile={rankedProfile}
                  latestDelta={ratingHistory[0]?.delta ?? null}
                  voice="third"
                  subjectName={child.firstName}
                />
              </CardContent>
            </Card>
          )}

          {/* Every quiz, live and self-paced. A live quiz with too small a
              field appears here as "Didn't count" — it would be missing
              entirely from a view built on rating history. */}
          <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
            <CardHeader title="Quiz Results" />
            <CardContent>
              <QuizResultsTable
                results={quizResults}
                loading={rankedLoading}
                emptyMessage={`${child.firstName} hasn't taken any quizzes yet.`}
              />
            </CardContent>
          </Card>

          {/* Progress Metrics */}
          <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
            <CardHeader title="Progress Metrics" />
            <Divider />
            <CardContent>
              {progress ? (
                <>
                  <Grid container spacing={3}>
                    <Grid item xs={6} sm={3}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: (theme) => `${theme.palette.primary.main}10`,
                          borderRadius: 2
                        }}
                      >
                        <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 40, mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">{progress.totalSubmissions}</Typography>
                        <Typography variant="body2" color="text.secondary">Problems Solved</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: (theme) => `${theme.palette.success.main}10`,
                          borderRadius: 2
                        }}
                      >
                        <TrendingUpIcon sx={{ color: 'success.main', fontSize: 40, mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">{progress.accuracyPercentage}%</Typography>
                        <Typography variant="body2" color="text.secondary">Accuracy Rate</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: (theme) => `${theme.palette.secondary.main}10`,
                          borderRadius: 2
                        }}
                      >
                        <TrophyIcon sx={{ color: 'secondary.main', fontSize: 40, mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold" data-testid="total-points">{progress.totalPoints.toFixed(0)}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Points</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: (theme) => `${theme.palette.warning.main}10`,
                          borderRadius: 2
                        }}
                      >
                        <TrophyIcon sx={{ color: 'warning.main', fontSize: 40, mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">{progress.achievementsEarned}</Typography>
                        <Typography variant="body2" color="text.secondary">Achievements</Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Correct Submissions
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {progress.correctSubmissions} of {progress.totalSubmissions}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Last Submission
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {formatDate(progress.lastSubmissionDate)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  No progress data available
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Enrollments */}
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardHeader
              title="Enrollments"
              subheader={`${enrollments.length} total enrollment${enrollments.length !== 1 ? 's' : ''}`}
            />
            <Divider />
            <CardContent>
              {enrollments.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No enrollments found
                  </Typography>
                </Box>
              ) : (
                <List>
                  {enrollments.map((enrollment, index) => {
                    const status = getEnrollmentStatus(enrollment);
                    return (
                      <React.Fragment key={enrollment.enrollmentId}>
                        <ListItem
                          sx={{
                            px: 0,
                            py: 2,
                            flexDirection: 'column',
                            alignItems: 'flex-start'
                          }}
                        >
                          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EventIcon color="primary" />
                              <Typography variant="h6">
                                Session #{enrollment.sessionId}
                              </Typography>
                            </Box>
                            <Chip
                              label={status.label}
                              color={status.color}
                              size="small"
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" component="span">
                              <CalendarIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                              Enrolled: {formatDate(enrollment.enrollmentDate)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="span">
                              Type: {enrollment.enrollmentType === 'individual' ? 'Individual' : 'Team'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="span">
                              Amount: ${enrollment.amountPaid.toFixed(2)}
                            </Typography>
                          </Box>
                        </ListItem>
                        {index < enrollments.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Summary */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
            <CardHeader title="Enrollment Summary" />
            <Divider />
            <CardContent>
              {progress ? (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Enrollments
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {progress.totalEnrollments}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Active Enrollments
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {progress.activeEnrollments}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Your child is actively participating in {progress.activeEnrollments} session{progress.activeEnrollments !== 1 ? 's' : ''}.
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No enrollment data available
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardHeader title="Quick Stats" />
            <Divider />
            <CardContent>
              {progress ? (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Average Performance
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {progress.accuracyPercentage >= 90 ? 'Excellent' :
                       progress.accuracyPercentage >= 75 ? 'Good' :
                       progress.accuracyPercentage >= 60 ? 'Fair' : 'Needs Improvement'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {progress.totalSubmissions > 0
                      ? `Your child has solved ${progress.totalSubmissions} problems with ${progress.accuracyPercentage}% accuracy.`
                      : 'No submissions yet. Encourage your child to start solving problems!'}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No stats available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </MainLayout>
  );
}

export default ChildDetailPage;
